import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import classNames from 'classnames';
import FcraForm from './FcraForm';
import utils from '../../helpers/utils';
import {useFcraConsent} from '../../customHooks/useFcraConsent';
import {RootState} from '../../reducers/rootReducer';
import useWhiteLabelClient from '../../customHooks/useWhiteLabelClient';
import renderUtils from '../../helpers/renderUtils';

type Props = {
  fcra?: $TSFixMe;
  showErrorMsg: boolean;
  showFcraColp: boolean;
  localizedStringBundle: StringMap;
  isAgent: boolean;
};

const FcraModal = ({showErrorMsg, showFcraColp, localizedStringBundle, isAgent}: Props) => {
  const [checked, setChecked] = useState(false);
  const {acceptFcra} = useFcraConsent();
  const auth = useSelector((store: RootState) => store.auth);
  const whiteLabelClient = useWhiteLabelClient();
  const isReimagineThemeEligible = utils.isReimagineThemeEligible(auth);

  useEffect(() => {
    setTimeout(() => {
      utils.resizeIframe(undefined, document.body.clientHeight + 600);
    }, 500);
  });

  const handleSubmit = (e: $TSFixMe) => {
    e.preventDefault();
    acceptFcra();
  };

  const renderFcraErrorMsg = () => {
    return (
      <div className="py-24" data-testid="fcra-error-msg-container">
        {renderUtils.renderErrorOutlineIcon({
          isReimagineThemeEligible,
          whiteLabelClient,
          // reimagine-ignore — icon glyph size / material icon glyph, not themed text
          classes: 'error-color block text-center text-7xl text-critical',
        })}
        <p className="reimagine-text-h3 m-5 block text-center text-2xl text-onSurface-primary">
          {localizedStringBundle.COMMON_ERR_MSG}
        </p>
      </div>
    );
  };

  const renderFcraContent = (checked: $TSFixMe, setChecked: $TSFixMe) => {
    // Provide custom text message and color for subtitle shown in agent flow (explain that app access is blocked)
    const modalSubTitle = isAgent
      ? localizedStringBundle.FCRA_MODAL_SUBTITLE_AGENT
      : localizedStringBundle.FCRA_MODAL_SUBTITLE;
    const subtitleClassName = classNames(
      'reimagine-text-h3 block my-4 text-xl font-semibold text-center md:text-2xl',
      isAgent ? 'text-critical' : 'text-onSurface-primary'
    );

    return (
      <>
        <div
          className="reimagine-text-h1 mx-4 my-5 text-center text-2xl font-semibold text-onSurface-primary md:mx-0 md:text-4xl"
          data-testid="fcra-modal-title"
        >
          {localizedStringBundle.FCRA_MODAL_TITLE}
          <span className={subtitleClassName} data-testid="fcra-modal-subtitle">
            {' '}
            {modalSubTitle}
          </span>
        </div>

        <div className="mx-4 my-8 flex md:mx-8 md:my-16" data-testid="fcra-modal-checkbox">
          <input
            className="mt-2"
            type="checkbox"
            id="fcraCheck"
            checked={checked}
            disabled={isAgent}
            onChange={() => setChecked(!checked)}
          />
          <label
            className="reimagine-text-body1 ml-2 text-left leading-normal text-onSurface-primary"
            htmlFor="fcraCheck"
          >
            {utils.parseHtml(localizedStringBundle.FCRA_ACCEPT_CONTENT_TEXT)}
          </label>
        </div>
        <span
          className="reimagine-text-body1 mx-8 my-8 block text-onSurface-primary md:mx-18 md:my-16"
          data-testid="fcra-modal-accept-text"
        >
          {utils.parseHtml(localizedStringBundle.FCRA_ACCEPT_TEXT)}
        </span>
      </>
    );
  };

  if (showFcraColp) {
    return (
      <div className="w-auto flex-grow">
        <div className="absolute inset-0 border-none bg-surface text-onSurface-primary">
          <div className="md:m-32">
            {showErrorMsg ? (
              renderFcraErrorMsg()
            ) : (
              <>
                {renderFcraContent(checked, setChecked)}
                <FcraForm
                  checkBoxSelected={checked}
                  onSubmit={handleSubmit}
                  localizedStringBundle={localizedStringBundle}
                />
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
  return null;
};

export default FcraModal;
